import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Wrench, Camera, CheckCircle2, AlertTriangle, ClipboardList, ArrowLeft, MapPin } from 'lucide-react'
import api from '../api'
import { Button, Alert, Label, Card, Badge, Input, Select, Textarea } from '../components/ui'
import useBranding from '../useBranding'
import LokasiMap from '../components/LokasiMap'

// Tanggal lokal browser, format YYYY-MM-DD -- sama pola dgn hariIniLokal di PelangganKontak.jsx
// (SENGAJA bukan toISOString().slice(0,10), lihat komentar di sana soal jam dini hari WIB).
function hariIniLokal() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const textareaStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 10, minHeight: 80, resize: 'vertical',
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

const jenisLabel = { GNG: 'Gangguan', PSB: 'Pasang Baru', CAB: 'Cabut', REL: 'Relokasi', UPG: 'Upgrade', PME: 'Pemeliharaan', PSG: 'Pasang Perangkat', OPT: 'Optik', SRV: 'Survey' }

function Shell({ children, token }) {
  const businessName = useBranding()
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(circle at 20% 20%, #2b2f77 0%, #14152b 55%, #0b0b16 100%)',
      padding: 20
    }}>
      <div style={{ width: 420, maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, margin: '0 auto 12px',
            background: 'linear-gradient(135deg, var(--brand), #7c8cf8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(79,70,229,0.35)'
          }}>
            <Wrench size={22} color="#fff" strokeWidth={2} />
          </div>
          <h1 style={{ fontSize: 19, fontWeight: 800, color: '#fff', letterSpacing: -0.4 }}>{businessName} — Pekerjaan</h1>
          {token && (
            <Link to={`/pekerjaan-list/${token}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: 12.5, color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
              <ArrowLeft size={13} /> Kembali ke daftar
            </Link>
          )}
        </div>
        <Card style={{ padding: 28 }}>{children}</Card>
      </div>
    </div>
  )
}

export default function PekerjaanItemForm() {
  const { token, id } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [item, setItem] = useState(null)
  const [selectedNama, setSelectedNama] = useState([])
  const [catatan, setCatatan] = useState('')
  const [foto, setFoto] = useState(null)
  // Cuma dipakai utk job PSB (pasang baru) -- teknisi sekalian catat pelanggan baru LENGKAP tepat
  // saat menandai selesai (2026-07-22, perluasan akses PSB -- dulu cuma 3 field ini, admin lengkapi
  // billing belakangan lewat app; sekarang teknisi isi semua sendiri, admin tidak perlu ikut campur).
  const [pppUsername, setPppUsername] = useState('')
  const [noHp, setNoHp] = useState('')
  const [namaPelanggan, setNamaPelanggan] = useState('')
  const [nik, setNik] = useState('')
  const [npwp, setNpwp] = useState('')
  const [alamat, setAlamat] = useState('')
  const [pppPassword, setPppPassword] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [paketLanggananId, setPaketLanggananId] = useState('')
  const [paketList, setPaketList] = useState([])
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [lokasiLoading, setLokasiLoading] = useState(false)
  const [lokasiError, setLokasiError] = useState('')
  const lokasiMapRef = useRef(null)
  const [tanggalAktif, setTanggalAktif] = useState(hariIniLokal())
  const [metodeBayar, setMetodeBayar] = useState('PASCABAYAR')
  const [tanggalJatuhTempo, setTanggalJatuhTempo] = useState(11)
  const [statusPelanggan, setStatusPelanggan] = useState('AKTIF')
  const [submittingSelesai, setSubmittingSelesai] = useState(false)
  const [submittingProses, setSubmittingProses] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [prosesInfo, setProsesInfo] = useState('')
  const [done, setDone] = useState(false)
  const [tagihanProrataInfo, setTagihanProrataInfo] = useState('')

  useEffect(() => { loadItem() }, [token, id])
  useEffect(() => { api.get('/public/paket-langganan').then(res => setPaketList(res.data)).catch(() => {}) }, [])

  function ambilLokasi() {
    if (!navigator.geolocation) { setLokasiError('Browser ini tidak mendukung Geolocation'); return }
    setLokasiLoading(true)
    setLokasiError('')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6)
        const lng = pos.coords.longitude.toFixed(6)
        setLatitude(lat); setLongitude(lng)
        lokasiMapRef.current?.setPosition(lat, lng)
        setLokasiLoading(false)
      },
      (err) => { setLokasiError('Gagal ambil lokasi: ' + err.message); setLokasiLoading(false) },
      { enableHighAccuracy: true, timeout: 15000 }
    )
  }

  function loadItem() {
    setLoading(true)
    api.get(`/pekerjaan-list/${token}/${id}`)
      .then(res => {
        setItem(res.data)
        setSelectedNama(res.data.teknisiTerpasang || [])
      })
      .catch(err => setError(err.response?.data?.message || 'Pekerjaan tidak ditemukan'))
      .finally(() => setLoading(false))
  }

  function toggleNama(nama) {
    setSelectedNama(s => s.includes(nama) ? s.filter(n => n !== nama) : [...s, nama])
  }

  async function handleSelesai(e) {
    e.preventDefault()
    setSubmitError(''); setProsesInfo('')
    if (selectedNama.length === 0) return setSubmitError('Pilih minimal satu teknisi')
    if (!foto) return setSubmitError('Foto bukti wajib dilampirkan')
    const isPsb = item.jenisPekerjaanKode === 'PSB'
    if (isPsb && (!pppUsername.trim() || !noHp.trim() || !namaPelanggan.trim())) {
      return setSubmitError('Username PPP, nomor HP, dan nama pelanggan wajib diisi utk PSB')
    }
    if (isPsb && !nik.trim()) {
      return setSubmitError('NIK wajib diisi utk PSB')
    }
    if (isPsb && metodeBayar === 'PRABAYAR' && !paketLanggananId) {
      return setSubmitError('Paket Langganan wajib dipilih utk pelanggan Prabayar (dipakai hitung tagihan prorata)')
    }

    setSubmittingSelesai(true)
    try {
      const fd = new FormData()
      fd.append('nama', selectedNama.join(','))
      fd.append('catatan', catatan)
      fd.append('foto', foto)
      if (isPsb) {
        fd.append('pppUsername', pppUsername.trim())
        fd.append('noHp', noHp.trim())
        fd.append('namaPelanggan', namaPelanggan.trim())
        fd.append('nik', nik.trim())
        if (npwp.trim()) fd.append('npwp', npwp.trim())
        if (alamat.trim()) fd.append('alamat', alamat.trim())
        if (pppPassword.trim()) fd.append('pppPassword', pppPassword.trim())
        if (ipAddress.trim()) fd.append('ipAddress', ipAddress.trim())
        if (paketLanggananId) fd.append('paketLanggananId', paketLanggananId)
        if (latitude) fd.append('latitude', latitude)
        if (longitude) fd.append('longitude', longitude)
        fd.append('tanggalAktif', tanggalAktif)
        fd.append('metodeBayar', metodeBayar)
        fd.append('tanggalJatuhTempo', tanggalJatuhTempo)
        fd.append('statusPelanggan', statusPelanggan)
      }
      const res = await api.post(`/pekerjaan-list/${token}/${id}/selesai`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (res.data.tagihan_prorata) {
        const tp = res.data.tagihan_prorata
        setTagihanProrataInfo(`Tagihan prorata dibuat: Rp ${Number(tp.jumlah).toLocaleString('id-ID')} (${tp.sisa_hari} hari, periode ${tp.periode}).`)
      }
      setDone(true)
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Gagal menyimpan')
    } finally { setSubmittingSelesai(false) }
  }

  async function handleProses() {
    setSubmitError(''); setProsesInfo('')
    if (!catatan.trim()) return setSubmitError('Isi catatan progres dulu')

    setSubmittingProses(true)
    try {
      const fd = new FormData()
      fd.append('nama', selectedNama.join(','))
      fd.append('catatan', catatan)
      if (foto) fd.append('foto', foto)
      await api.post(`/pekerjaan-list/${token}/${id}/proses`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setProsesInfo('Catatan tersimpan.')
      setCatatan('')
      setFoto(null)
      loadItem()
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Gagal menyimpan')
    } finally { setSubmittingProses(false) }
  }

  if (loading) return <Shell><p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Memuat...</p></Shell>

  if (error) return (
    <Shell token={token}>
      <div style={{ textAlign: 'center' }}>
        <AlertTriangle size={32} color="var(--danger)" style={{ marginBottom: 10 }} />
        <p style={{ color: 'var(--text)', fontWeight: 600 }}>{error}</p>
      </div>
    </Shell>
  )

  if (done || item.status === 'SELESAI') return (
    <Shell token={token}>
      <div style={{ textAlign: 'center' }}>
        <CheckCircle2 size={36} color="var(--success)" style={{ marginBottom: 10 }} />
        <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
          {done ? 'Tersimpan, terima kasih!' : 'Pekerjaan ini sudah pernah diselesaikan sebelumnya.'}
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>{item.lokasi}</p>
        {done && tagihanProrataInfo && <p style={{ color: 'var(--brand)', fontSize: 12.5, marginTop: 10, fontWeight: 600 }}>{tagihanProrataInfo}</p>}
      </div>
    </Shell>
  )

  return (
    <Shell token={token}>
      <div style={{ marginBottom: 20, padding: 14, borderRadius: 12, background: 'var(--brand-soft)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 700, color: 'var(--brand-soft-text)' }}>
          <ClipboardList size={16} /> {item.lokasi}
          <Badge tone="neutral" style={{ marginLeft: 'auto' }}>{jenisLabel[item.jenisPekerjaanKode] || item.jenisPekerjaanKode}</Badge>
        </div>
        {item.detail && <p style={{ fontSize: 13, color: 'var(--text)', margin: 0 }}>{item.detail}</p>}
        {item.catatanTerakhir && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Catatan terakhir: {item.catatanTerakhir}</p>}
      </div>

      {prosesInfo && <Alert type="success">{prosesInfo}</Alert>}

      <form onSubmit={handleSelesai}>
        {submitError && <Alert type="error">{submitError}</Alert>}

        <div style={{ marginBottom: 16 }}>
          <Label>Teknisi yang mengerjakan</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {item.teknisi.map(t => {
              const active = selectedNama.includes(t.nama)
              return (
                <button
                  type="button" key={t.id} onClick={() => toggleNama(t.nama)}
                  style={{
                    padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    border: active ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
                    background: active ? 'var(--brand-soft)' : 'transparent',
                    color: active ? 'var(--brand-soft-text)' : 'var(--text-muted)'
                  }}
                >{t.nama}</button>
              )
            })}
          </div>
        </div>

        {item.jenisPekerjaanKode === 'PSB' && (
          <div style={{ marginBottom: 16, padding: 12, borderRadius: 10, background: 'var(--brand-soft)' }}>
            {/* Form penuh (2026-07-22, perluasan akses PSB) -- teknisi isi semua data pelanggan
                sendiri di sini, admin tidak perlu lengkapi belakangan lewat app lagi. */}
            <Label>Daftarkan Pelanggan Baru (wajib utk Pasang Baru)</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <Input value={pppUsername} onChange={e => setPppUsername(e.target.value)} placeholder="Username PPP, mis. budi01" />
              <Input value={pppPassword} onChange={e => setPppPassword(e.target.value)} placeholder="Password PPPoE" />
              <Input value={noHp} onChange={e => setNoHp(e.target.value)} placeholder="Nomor HP, 08xx / 62xx / +62xx" />
              <Input value={namaPelanggan} onChange={e => setNamaPelanggan(e.target.value)} placeholder="Nama pelanggan" />
              <Input value={nik} onChange={e => setNik(e.target.value)} placeholder="NIK (16 digit KTP) *" />
              <Input value={npwp} onChange={e => setNpwp(e.target.value)} placeholder="NPWP (boleh dikosongkan)" />
              <Textarea rows={2} value={alamat} onChange={e => setAlamat(e.target.value)} placeholder="Alamat pelanggan" />
              <Select value={paketLanggananId} onChange={e => setPaketLanggananId(e.target.value)}>
                <option value="">- Pilih Paket Langganan -</option>
                {paketList.map(p => <option key={p.id} value={p.id}>{p.nama} (Rp {Number(p.harga_bulanan).toLocaleString('id-ID')})</option>)}
              </Select>
              <Input value={ipAddress} onChange={e => setIpAddress(e.target.value)} placeholder="IP Address (kalau IP statis)" />

              <div>
                <div className="grid-2" style={{ gap: 8, marginBottom: 8 }}>
                  <Input value={latitude} onChange={e => setLatitude(e.target.value)} placeholder="Latitude" />
                  <Input value={longitude} onChange={e => setLongitude(e.target.value)} placeholder="Longitude" />
                </div>
                <Button type="button" size="sm" variant="soft" icon={MapPin} loading={lokasiLoading} onClick={ambilLokasi}>Ambil Lokasi Saat Ini</Button>
                {lokasiError && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>{lokasiError}</div>}
                {latitude && longitude && (
                  <div style={{ marginTop: 10 }}>
                    <LokasiMap ref={lokasiMapRef} lat={latitude} lng={longitude} onChange={(lat, lng) => { setLatitude(lat); setLongitude(lng) }} />
                    <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>Geser peta sampai pin merah tepat di lokasi pelanggan.</div>
                  </div>
                )}
              </div>

              <div className="grid-2" style={{ gap: 8 }}>
                <div>
                  <Label style={{ fontSize: 11 }}>Tanggal Aktif</Label>
                  <Input type="date" value={tanggalAktif} onChange={e => setTanggalAktif(e.target.value)} style={{ marginTop: 4 }} />
                </div>
                <div>
                  <Label style={{ fontSize: 11 }}>Tgl Jatuh Tempo (1-28)</Label>
                  <Input type="number" min={1} max={28} value={tanggalJatuhTempo} onChange={e => setTanggalJatuhTempo(parseInt(e.target.value) || 1)} style={{ marginTop: 4 }} />
                </div>
              </div>
              <div className="grid-2" style={{ gap: 8 }}>
                <div>
                  <Label style={{ fontSize: 11 }}>Metode Bayar</Label>
                  <Select value={metodeBayar} onChange={e => setMetodeBayar(e.target.value)} style={{ marginTop: 4 }}>
                    <option value="PASCABAYAR">Pascabayar</option>
                    <option value="PRABAYAR">Prabayar</option>
                  </Select>
                </div>
                <div>
                  <Label style={{ fontSize: 11 }}>Status</Label>
                  <Select value={statusPelanggan} onChange={e => setStatusPelanggan(e.target.value)} style={{ marginTop: 4 }}>
                    <option value="AKTIF">Aktif</option>
                    <option value="ISOLIR">Isolir</option>
                    <option value="BERHENTI">Berhenti</option>
                  </Select>
                </div>
              </div>
              {metodeBayar === 'PRABAYAR' && (
                <div style={{ fontSize: 11.5, color: 'var(--brand-soft-text)', background: '#fff', borderRadius: 8, padding: '8px 10px' }}>
                  Prabayar: tagihan prorata langsung dibuat otomatis begitu ditandai Selesai.
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Label>Catatan</Label>
          <textarea
            style={{ ...textareaStyle, marginTop: 6 }}
            value={catatan} onChange={e => setCatatan(e.target.value)}
            placeholder="Update progres, atau catatan penyelesaian"
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>Foto {'(wajib kalau "Selesai", opsional kalau update Proses)'}</Label>
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 6, padding: '16px', borderRadius: 10, cursor: 'pointer',
            border: '1.5px dashed var(--border-strong)', color: 'var(--text-muted)', fontSize: 13
          }}>
            <Camera size={18} />
            {foto ? foto.name : 'Ambil / pilih foto'}
            <input
              type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={e => setFoto(e.target.files[0] || null)}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            type="button" variant="neutral" loading={submittingProses} disabled={submittingSelesai}
            onClick={handleProses} style={{ flex: 1, padding: '12px' }}
          >
            {submittingProses ? 'Menyimpan...' : 'Update Proses'}
          </Button>
          <Button type="submit" loading={submittingSelesai} disabled={submittingProses} style={{ flex: 1, padding: '12px' }}>
            {submittingSelesai ? 'Menyimpan...' : 'Tandai Selesai'}
          </Button>
        </div>
      </form>
    </Shell>
  )
}
